import { ChangeRideStatusFormType } from "../components/features/course/change-ride-status";
import {
  CourseEstimateRequest,
  CourseEstimateResult,
} from "../types/course-estimate";

export class CourseService {
  constructor(private readonly apiUrl: string) {}

  async estimateCourse(
    request: CourseEstimateRequest,
  ): Promise<CourseEstimateResult> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const resultat = await fetch(`${this.apiUrl}/api/courses/estimate`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    });

    if (!resultat.ok) {
      throw new Error(`Erreur HTTP: ${resultat.status}`);
    }

    return (await resultat.json()) as CourseEstimateResult;
  }

  async getAllCourses() {
    try {
      const resultat = await fetch(`${this.apiUrl}/api/courses/`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async getAllCoursesByIdClient(idClient: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/client/${idClient}`,
        {
          method: "GET",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async getCourseById(id: string) {
    try {
      const resultat = await fetch(`${this.apiUrl}/api/courses/${id}`, {
        method: "GET",
        cache: "no-store",
        headers: {},
      });

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async createCourse(course: any) {
    try {
      const resultat = await fetch(`${this.apiUrl}/api/courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(course),
      });

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async createCourseAnonyme(course: any) {
    try {
      const resultat = await fetch(`${this.apiUrl}/api/courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(course),
      });

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async updateCourse(courseId: number, course: any) {
    try {
      const resultat = await fetch(`${this.apiUrl}/api/courses/${courseId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(course),
      });

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async cancelCourse(courseId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/cancel`,
        {
          method: "POST",
          headers: {},
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des cours:", error);
      throw error;
    }
  }

  async changeStatusRide(courseId: number, data: ChangeRideStatusFormType) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/statut`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            statut: data.statut,
            dateChangement: new Date(),
            commentaire: data.commentaire,
          }),
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour du statut de la course:",
        error,
      );
      throw error;
    }
  }

  async createPayment(courseId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/create-checkout-session`,
        {
          method: "POST",
        },
      );

      return await resultat.json();
    } catch (error) {
      console.error("Erreur lors du paiement:", error);
      throw error;
    }
  }

  async assigningDriverToRide(courseId: number, driverId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/affectation/chauffeur/${driverId}`,
        {
          method: "PATCH",
          headers: {},
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de l'affectation:", error);
      throw error;
    }
  }

  async decommissioningDriverFromRide(courseId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/desaffectation/chauffeur`,
        {
          method: "PATCH",
          headers: {},
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de la désaffectation:", error);
      throw error;
    }
  }

  async assignmentVehicleCourse(courseId: number, vehicleId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/affectation/voiture/${vehicleId}`,
        {
          method: "PATCH",
          headers: {},
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de l'affectation:", error);
      throw error;
    }
  }

  async decommissioningVehicleCourse(courseId: number) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/desaffectation/voiture`,
        {
          method: "PATCH",
          headers: {},
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de la désaffectation:", error);
      throw error;
    }
  }

  async addRating(courseId: number, rating: any) {
    try {
      const resultat = await fetch(
        `${this.apiUrl}/api/courses/${courseId}/avis`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(rating),
        },
      );

      if (!resultat.ok) {
        throw new Error(`Erreur HTTP: ${resultat.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout de la note':", error);
      throw error;
    }
  }
}
